import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { NAV_SECTIONS, type NavItem } from '@/constants/navigation';

/**
 * Screen favourites — a client-side clone of the myPOS ONE behaviour (GAP: dead Favourite buttons).
 *
 * A favourite is just a navigation route path (a leaf NavItem's `path`), kept as a JSON array of
 * strings in localStorage. Toolbar stars toggle the current screen in/out of that list; the sidebar
 * reads the same list and renders a pinned "Favourites" group. Cross-component sync is a custom
 * window event (same tab) plus the native `storage` event (other tabs) — no store, no backend, so it
 * is per-browser, exactly as myPOS ONE does it.
 */
const LS_FAVOURITES = 'myledger:sidebar:favourites';
export const FAVOURITES_CHANGED_EVENT = 'myledger:favourites-changed';

/** Every leaf nav item (one that navigates), flattened from the section tree. */
function leafItems(): NavItem[] {
  const out: NavItem[] = [];
  const walk = (items: NavItem[]) => {
    for (const it of items) {
      if (it.children?.length) walk(it.children);
      else if (it.path) out.push(it);
    }
  };
  walk(NAV_SECTIONS);
  return out;
}

/** Resolve a URL to the closest navigable nav item — detail routes fold up to their list screen. */
function resolveNavItem(pathname: string): NavItem | null {
  let best: NavItem | null = null;
  for (const it of leafItems()) {
    const p = it.path!;
    if (pathname === p || pathname.startsWith(p + '/')) {
      // Prefer the longest matching path (so /banking/deposits/new resolves to /banking/deposits,
      // not to a shorter /banking prefix if one existed).
      if (!best || p.length > best.path!.length) best = it;
    }
  }
  return best;
}

export function readFavourites(): string[] {
  try {
    const raw = localStorage.getItem(LS_FAVOURITES);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

function writeFavourites(list: string[]): void {
  try {
    localStorage.setItem(LS_FAVOURITES, JSON.stringify(list));
  } catch {
    /* storage full / unavailable — favourites are best-effort */
  }
  window.dispatchEvent(new CustomEvent(FAVOURITES_CHANGED_EVENT));
}

/** Subscribe to favourite changes (same-tab custom event + cross-tab storage event). */
export function useFavourites(): string[] {
  const [favs, setFavs] = useState<string[]>(readFavourites);
  useEffect(() => {
    const refresh = () => setFavs(readFavourites());
    window.addEventListener(FAVOURITES_CHANGED_EVENT, refresh);
    window.addEventListener('storage', refresh);
    return () => {
      window.removeEventListener(FAVOURITES_CHANGED_EVENT, refresh);
      window.removeEventListener('storage', refresh);
    };
  }, []);
  return favs;
}

/**
 * Favourite state for the current screen. `navItem` is null when the URL is not a nav target, in
 * which case callers should hide their star and `toggle` is a no-op.
 */
export function useSidebarFavorite(pathOverride?: string): {
  navItem: NavItem | null;
  isFavorite: boolean;
  toggle: () => void;
  label: string;
} {
  const location = useLocation();
  const pathname = pathOverride ?? location.pathname;
  const navItem = resolveNavItem(pathname);
  const favs = useFavourites();
  const key = navItem?.path ?? null;
  const isFavorite = !!key && favs.includes(key);

  const toggle = useCallback(() => {
    if (!key) return;
    const current = readFavourites();
    const next = current.includes(key) ? current.filter(p => p !== key) : [...current, key];
    writeFavourites(next);
  }, [key]);

  return { navItem, isFavorite, toggle, label: navItem?.label ?? '' };
}
